import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { api } from '../api';
import './predictNight.css';
import LOGO_FIT from './pnLogoFit.json';
import PN_LOGO from './pnLogoSticker.png';

// PREDICT THE NIGHT — a full-screen swipe game over tonight's games.
// Drag left = the right panel (home) takes over and wins, drag right = the left panel (away) wins. $25 to enter a night; whoever gets the
// most picks right wins $125 (split on a tie). The server grades
// the picks and pays the prize (server/settle.js gradePredictions). Opens from a banner on the Games page.
const FEE = 25;
const PRIZE = 125;   // the night's winner (most right picks) gets this; a tie splits it
const NOT_OPEN = ['LIVE', 'CRIT', 'FINAL', 'OFF', 'PPD'];
const DAY = 24 * 3600 * 1000;
const THROW_X = 110;      // px dragged = a pick
const THROW_V = 0.55;     // or a flick this fast (px/ms)
const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const SIDES = ['away', 'home'];
// DEV ONLY: localhost/?pnMock opens the swipe screen without paying. Nothing is saved.
const DEV_MOCK = process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('pnMock');

export default function PredictNight({ lang, userId, games, nbaGames, mlbGames, nflGames, logoUrl, logoUrlDark, teamColor, teamColor2, haptic, balance, onBalance, money }) {
  const L = (en, fr) => (lang === 'fr' ? fr : en);
  const [view, setView] = useState(DEV_MOCK ? 'swipe' : null);        // null (closed) | 'enter' | 'swipe'
  const [entries, setEntries] = useState([]);    // { user_id, game_date, fee, payout, paid_out_at }
  const [practice, setPractice] = useState(false); // tables not in the database yet: play without saving
  const [paying, setPaying] = useState(false);
  const [showBoard, setShowBoard] = useState(false);
  const [records, setRecords] = useState({});   // sport -> { ABBREV: 'W-L' } from the site's standings   // the standings, opened inside the banner
  const [picks, setPicks] = useState({});        // game_id -> my row
  const [board, setBoard] = useState([]);
  const [names, setNames] = useState({});
  const [msg, setMsg] = useState('');
  const [drag, setDrag] = useState({ x: 0, y: 0, on: false });
  const [fly, setFly] = useState(null);          // { side, y } while the top card is thrown
  const [flash, setFlash] = useState(null);      // the picked team taking over the screen
  const armedRef = useRef(null);                 // past the point of no return (a tick when crossing it)
  const [redo, setRedo] = useState([]);          // games sent back with Undo (shown again first)
  const track = useRef(null);
  const [minute, setMinute] = useState(() => Math.floor(Date.now() / 60000));
  useEffect(() => { const id = setInterval(() => setMinute(Math.floor(Date.now() / 60000)), 30000); return () => clearInterval(id); }, []);
  // the swipe screen is drawn at one size and scaled as a single picture; its panels run past it to the window edges (phones keep their own layout)
  const [fit, setFit] = useState(null);
  useEffect(() => {
    const measure = () => {
      if (window.innerWidth <= 720 && window.innerHeight > window.innerWidth) { setFit(null); return; }
      const scale = Math.min(window.innerWidth / 1705, window.innerHeight / 1266);
      const extra = (window.innerWidth / scale - 1705) / 2;   // how far the panels run past the design on each side
      const mid = 1 + 2 * extra / 1705;
         const tip = 221.65 * (mid - 1);
         // how far the panel's contents sit from its tip, in proportion to the wider panel
         const inward = 0.5675 * (extra - tip);
         setFit({ '--pn-s': scale, '--pn-x': `${extra}px`, '--pn-y': `${(window.innerHeight / scale - 1266) / 2}px`, '--pn-mid': Math.min(mid, 1.7), '--pn-vt': `${(48 - 8 * Math.min(1, (mid - 1) / 0.1)) / Math.min(mid, 1.7)}%`, '--pn-vb': `${(68 - 8 * Math.min(1, (mid - 1) / 0.1)) / Math.min(mid, 1.7)}%`, '--pn-holo': Math.min(1 + (mid - 1) / 2, 1.35), '--pn-tip': `${tip}px`, '--pn-in': `${inward}px`, '--pn-grow': 1 + extra / 1700 });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // tonight's slate: every game of the next 24 h that hasn't started, earliest first
  const upcoming = useMemo(() => {
    const now = minute * 60000;
    const tag = (list, sport) => (list || []).map((g) => ({ ...g, _sport: sport }));
    return [...tag(games, 'nhl'), ...tag(nbaGames, 'nba'), ...tag(mlbGames, 'mlb'), ...tag(nflGames, 'nfl')]
      .filter((g) => {
        const t = new Date(g.game_time).getTime();
        return g.home_abbrev && g.away_abbrev && !NOT_OPEN.includes(g.status) && t > now && t - now < DAY;
      })
      .sort((a, b) => new Date(a.game_time) - new Date(b.game_time));
  }, [games, nbaGames, mlbGames, nflGames, minute]);
  // the night = the date of the next game, and its players
  // a night that has players stays the night until its prize is paid (the server pays the morning
  // after, once every game is graded) — so its standings don't vanish when its last game starts
  const running = entries.filter((e) => !e.paid_out_at && e.game_date >= localDate(new Date(Date.now() - 2 * DAY))).map((e) => e.game_date).sort()[0];
  const night = running || (upcoming.length ? localDate(new Date(upcoming[0].game_time)) : localDate(new Date()));
  const slate = useMemo(() => upcoming.filter((g) => localDate(new Date(g.game_time)) === night), [upcoming, night]);
  const nightEntries = entries.filter((e) => e.game_date === night);
  const entered = practice || nightEntries.some((e) => e.user_id === userId);
  const redoGames = redo.map((id) => slate.find((g) => String(g.id) === id)).filter(Boolean);
  const deck = [...redoGames, ...slate.filter((g) => !picks[String(g.id)] && !redo.includes(String(g.id)))];
  const card = deck[0];
  const picked = slate.filter((g) => picks[String(g.id)] && !redo.includes(String(g.id))).length;

  // ── data ──
  const load = useCallback(async () => {
    if (!userId) return;
    if (DEV_MOCK) { setPractice(true); return; }
    const since = localDate(new Date(Date.now() - 60 * DAY));
    const { data, error } = await supabase.from('predictions').select('user_id, game_id, sport, game_date, pick, result').gte('game_date', since).limit(5000);
    if (error) {
      setPractice(true);
      setMsg(L('Practice mode: nothing is saved and there is no prize yet — the Predict tables are missing in the database.', 'Mode pratique : rien n\'est enregistré et pas de prix — les tables de Prédis manquent dans la base de données.'));
      return;
    }
    setPractice(false);
    setMsg('');
    setBoard(data || []);
    const { data: ent } = await supabase.from('predict_entries').select('user_id, game_date, fee, payout, paid_out_at').gte('game_date', since).limit(5000);
    setEntries(ent || []);
    setPicks(Object.fromEntries((data || []).filter((p) => p.user_id === userId).map((p) => [String(p.game_id), p])));
    const ids = [...new Set([...(data || []), ...(ent || [])].map((p) => p.user_id))];
    if (ids.length) {
      const { data: prof } = await supabase.from('profiles').select('id, username').in('id', ids);
      setNames(Object.fromEntries((prof || []).map((p) => [p.id, p.username])));
    }
  }, [userId, lang]); // eslint-disable-line
  useEffect(() => { load(); const id = setInterval(load, 60 * 1000); return () => clearInterval(id); }, [load]);
  // each team's record (the same standings as the Standings tab); NHL shows W-L-OTL
  useEffect(() => {
    if (!view) return;
    ['nhl', 'nba', 'mlb'].filter((sp) => !records[sp]).forEach((sp) => {
      api.fetchStandings(sp).then((d) => {
        const teams = d?.groups?.league?.[0]?.teams || [];
        setRecords((r) => ({ ...r, [sp]: Object.fromEntries(teams.map((t) => [t.abbrev, sp === 'nhl' ? `${t.w}-${t.l}-${t.otl}` : `${t.w}-${t.l}`])) }));
      }).catch(() => {});
    });
  }, [view]); // eslint-disable-line
  const recordOf = (g, side) => (g._sport === 'nfl' ? g[`${side}_record`] : records[g._sport]?.[g[`${side}_abbrev`]]) || '';

  const save = useCallback(async (g, pick) => {
    const id = String(g.id);
    const before = picks[id];
    const row = { user_id: userId, game_id: id, sport: g._sport, game_date: localDate(new Date(g.game_time)), pick, result: null };
    if (practice) { setPicks((p) => ({ ...p, [id]: row })); return; }
    setPicks((p) => ({ ...p, [id]: row }));
    let error;
    if (before) ({ error } = await supabase.from('predictions').update({ pick, updated_at: new Date().toISOString() }).eq('user_id', userId).eq('game_id', id));
    else ({ error } = await supabase.from('predictions').insert({ user_id: userId, game_id: id, sport: g._sport, game_date: row.game_date, pick }));
    if (error && (error.code === 'PGRST205' || error.code === '42P01' || /schema cache|does not exist/i.test(error.message || ''))) {
      // the table isn't there yet: keep playing, but say plainly that nothing is saved
      setMsg(L('Practice mode: picks are NOT saved yet — the predictions table is missing in the database.', 'Mode pratique : les choix ne sont PAS enregistrés — la table des prédictions manque dans la base de données.'));
    } else if (error) {
      setPicks((p) => { const n = { ...p }; if (before) n[id] = before; else delete n[id]; return n; });
      setMsg(L('That pick could not be saved (the game may have started).', 'Ce choix n\'a pas pu être enregistré (le match est peut-être commencé).'));
    } else {
      setBoard((b) => [...b.filter((x) => !(x.user_id === userId && String(x.game_id) === id)), row]);
    }
  }, [picks, userId, lang, practice]); // eslint-disable-line

  const enter = async () => {
    if (practice) { setView('swipe'); return; }
    setPaying(true);
    const { data, error } = await supabase.rpc('enter_predict_night', { p_date: night });
    setPaying(false);
    if (error || !data?.success) {
      const why = data?.error === 'funds' ? L('Not enough money in your balance.', 'Pas assez d\'argent dans ton solde.')
        : data?.error === 'already' ? L('You are already in.', 'Tu es déjà inscrit.')
        : L('Could not enter right now.', 'Impossible d\'entrer pour le moment.');
      setMsg(why);
      if (data?.error === 'already') { await load(); setView('swipe'); }
      return;
    }
    haptic && haptic('placed');
    onBalance && onBalance(parseFloat(data.new_balance));
    setEntries((e) => [...e, { user_id: userId, game_date: night, fee: FEE, payout: 0, paid_out_at: null }]);
    setMsg('');
    setView('swipe');
  };
  const open = () => {
    if (!entered) return setView('enter');
    if (!card) setRedo(slate.map((g) => String(g.id)));   // everything picked: swipe them again to change picks
    setView('swipe');
  };

  // ── swiping ──
  const colorOf = (g, side) => teamColor(g._sport, g[`${side}_abbrev`], g, side) || (side === 'home' ? '#ff5722' : '#3b82f6');
  // dark = the versions made for dark backgrounds (the full screen); NHL logos get the site's bigger size (class 'nhl')
  const logoOf = (g, side, dark = true) => (g._sport === 'nfl' ? g[`${side}_logo`] : (dark && logoUrlDark ? logoUrlDark : logoUrl)(g._sport, g[`${side}_abbrev`]));
  const lg = (g) => (g._sport === 'nhl' ? 'nhl' : '');
  // the swipe screen shows each logo with its sticker already drawn into an image (scripts/bake-logos.js); a team without one gets the live filter
  const bakedOf = (g, side) => (LOGO_FIT[g._sport]?.[g[`${side}_abbrev`]] ? `/logos-baked/${!fit || window.innerHeight <= 600 ? 'phone' : 'full'}/${g._sport}/${g[`${side}_abbrev`]}.webp` : null);
  const bigOf = (g, side) => { const b = bakedOf(g, side); return b && b.replace('/full/', '/big/'); };
  // touch screens get the back logo's tint baked into the image, so a swipe doesn't run the filter on every frame
  const darkOf = (g, side) => { const b = bakedOf(g, side); return b && window.matchMedia('(hover: none) and (pointer: coarse)').matches ? b.replace(/\/logos-baked\/\w+\//, '/logos-baked/phone-dark/') : null; };
  const bakedFailed = (g, side) => (e) => { e.currentTarget.classList.remove('pn-baked', 'pn-dark'); e.currentTarget.onerror = null; e.currentTarget.src = logoOf(g, side); };
  // the logos of tonight's games are downloaded as soon as the slate is known, before anyone opens the swipe screen; only the two games up next are decoded and kept in memory, so nothing pops in and phones don't run out
  const logoFiles = (g) => [...new Set(SIDES.flatMap((side) => (bakedOf(g, side) ? [bakedOf(g, side), bigOf(g, side), darkOf(g, side)] : [logoOf(g, side)])))].filter(Boolean);
  const decoded = useRef([]);
  useEffect(() => { slate.forEach((g) => logoFiles(g).forEach((src) => fetch(src, { mode: 'no-cors' }).catch(() => {}))); }, [slate.map((g) => g.id).join(), !!fit]); // eslint-disable-line
  useEffect(() => {
    decoded.current = deck.slice(0, 2).flatMap(logoFiles).map((src) => {
      const img = Object.assign(new Image(), { src });
      if (img.decode) img.decode().catch(() => {});
      return img;
    });
  }, [deck[0] && deck[0].id, deck[1] && deck[1].id, !!fit]); // eslint-disable-line
  // every logo's longest side drawn the same length: [width, height] is how much of its image the logo really fills
  const fitOf = (g, side) => {
    const m = LOGO_FIT[g._sport]?.[g[`${side}_abbrev`]];
    return m ? { '--fit': (0.76 / Math.max(m[0], m[1])).toFixed(3) } : undefined;
  };

  const choose = useCallback((side, y = 0, v = 0) => {
    if (!card || fly) return;
    haptic && haptic('tap');
    const key = Date.now();
    const speed = Math.min(3, Math.abs(v));                      // px/ms of the flick (0 for a button / key)
    const dur = Math.round(420 - speed * 70);                     // a hard flick leaves faster
    setFly({ side, y: y + v * 140, dur });
    setFlash({ key, side, game: card });
    setTimeout(() => setFlash((f) => (f && f.key === key ? null : f)), Math.max(240, dur - 80) + 400);
    const g = card;
    setTimeout(() => {
      save(g, side);
      setRedo((r) => r.filter((id) => id !== String(g.id)));
      setFly(null);
      setDrag({ x: 0, y: 0, on: false });
      if (deck.length === 1) { haptic && haptic('placed'); setView(null); }
    }, Math.max(240, dur - 80));
  }, [card, fly, deck.length, save, haptic]); // eslint-disable-line

  const undo = () => {
    const last = [...slate].reverse().find((g) => picks[String(g.id)] && !redo.includes(String(g.id)));
    if (last) setRedo((r) => [String(last.id), ...r]);
  };

  const down = (e) => {
    if (fly) return;
    track.current = { x0: e.clientX, y0: e.clientY, lx: e.clientX, lt: performance.now(), v: 0 };
    setDrag({ x: 0, y: 0, on: true });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const t = track.current;
    if (!t || !drag.on) return;
    const now = performance.now();
    if (now - t.lt > 8) { t.v = (e.clientX - t.lx) / (now - t.lt); t.lx = e.clientX; t.lt = now; }
    const nx = e.clientX - t.x0;
    const armed = nx > THROW_X ? 'away' : nx < -THROW_X ? 'home' : null;
    if (armed && armed !== armedRef.current) haptic && haptic('tap');
    armedRef.current = armed;
    setDrag({ x: nx, y: (e.clientY - t.y0) * 0.25, on: true });
  };
  const up = () => {
    const t = track.current;
    if (!t) return;
    track.current = null;
    armedRef.current = null;
    if (drag.x > THROW_X || (t.v > THROW_V && drag.x > 30)) choose('away', drag.y, t.v);
    else if (drag.x < -THROW_X || (t.v < -THROW_V && drag.x < -30)) choose('home', drag.y, t.v);
    else setDrag({ x: 0, y: 0, on: false });
  };

  // keyboard + body scroll lock while open
  useEffect(() => {
    if (!view) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setView(null);
      if (view !== 'swipe') return;
      if (e.key === 'ArrowLeft') choose('home');
      if (e.key === 'ArrowRight') choose('away');
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [view, choose]);

  // ── tonight's standings: the players who paid, by right picks ──
  const standings = useMemo(() => nightEntries.map((e) => {
    const mine = board.filter((p) => p.user_id === e.user_id && p.game_date === night);
    return { id: e.user_id, right: mine.filter((p) => p.result === 'won').length, done: mine.filter((p) => p.result).length, open: mine.filter((p) => !p.result).length, picks: mine.length };
  }).sort((a, b) => b.right - a.right || b.picks - a.picks), [nightEntries, board, night]);
  const lastNight = useMemo(() => {
    const paid = entries.filter((e) => e.paid_out_at && e.game_date < night);
    if (!paid.length) return null;
    const d = paid.map((e) => e.game_date).sort().pop();
    const rows = paid.filter((e) => e.game_date === d);
    return { date: d, winners: rows.filter((e) => Number(e.payout) > 0) };
  }, [entries, night]);

  const fmtDay = (g) => new Date(g.game_time).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', month: 'short', day: 'numeric' });
  const fmtHour = (g) => new Date(g.game_time).toLocaleTimeString(lang === 'fr' ? 'fr-CA' : 'en-CA', { hour: 'numeric', minute: '2-digit' });


  if (!slate.length && !nightEntries.length) return null;

  // ── the banner on the Games page ──
  const banner = (
    <div className={`pn-banner${showBoard ? ' open' : ''}`}>
      <div className="pn-banner-row">
        <img className="pn-banner-brand" src={PN_LOGO} alt={L('Predict the night', 'Prédis la soirée')} />
        <div className="pn-banner-text">
          <div className="pn-banner-chips">
            <span className="pn-chip pn-chip--accent">{L('Prize', 'Prix')} {money(PRIZE)}</span>
            <button type="button" className="pn-chip pn-chip--btn" onClick={() => setShowBoard((v) => !v)} aria-expanded={showBoard}>
              {nightEntries.length} {L(nightEntries.length === 1 ? 'player' : 'players', nightEntries.length === 1 ? 'joueur' : 'joueurs')}
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: showBoard ? 'rotate(180deg)' : 'none' }}><path d="M6 9l6 6 6-6" /></svg>
            </button>
            <span className="pn-chip">{entered && slate.length ? `${picked}/${slate.length} ${L('picked', 'choisis')}` : `${slate.length} ${L('games', 'matchs')}`}</span>
          </div>
        </div>
        {slate.length > 0 && (
          <button type="button" className="pn-banner-cta" onClick={open}>
            {!entered ? `${L('Enter', 'Entrer')} · ${money(FEE)}` : card ? L('Swipe', 'Glisse') : L('Change picks', 'Changer')}
          </button>
        )}
      </div>
      {showBoard && (
        <div className="pn-standings">
          {standings.length === 0 ? <p className="pn-standings-empty">{L('Nobody is in yet.', 'Personne n\'est inscrit encore.')}</p> : standings.map((u, i) => (
            <div key={u.id} className={`pn-standing${u.id === userId ? ' me' : ''}`}>
              <span className="pn-standing-rank">{i + 1}</span>
              <span className="pn-standing-name">{names[u.id] || '—'}</span>
              <span className="pn-standing-sub">{u.open ? `${u.open} ${L('to play', 'à jouer')}` : ''}</span>
              <span className="pn-standing-score">{u.right}<small>/{u.done}</small></span>
            </div>
          ))}
          {lastNight && (
            <p className="pn-standings-empty">{L('Last night', 'Hier')} · {lastNight.winners.length ? lastNight.winners.map((w) => `${names[w.user_id] || '—'} +${money(Number(w.payout))}`).join(', ') : L('no winner', 'aucun gagnant')}</p>
          )}
        </div>
      )}
    </div>
  );
  if (!view) return banner;

  // ── full screen ──
  const x = fly ? (fly.side === 'home' ? -1 : 1) * (window.innerWidth * 1.5) : drag.x;
  const y = fly ? fly.y : drag.y;
  const teamStyle = (side) => {
    const ease = drag.on && !fly ? 'none' : fly ? `transform ${fly.dur}ms cubic-bezier(.15, .7, .35, 1), opacity ${fly.dur}ms ease` : 'transform 0.5s cubic-bezier(.2, 1.4, .4, 1), opacity 0.3s ease';
    if (lean === side) {
      // travels with the panel's middle; on the throw it fades out
      return { transform: `translate(${(side === 'home' ? -1 : 1) * shiftOf(takeOf(side))}px, ${y * 0.5}px)`, opacity: fly ? 0 : Math.max(0, 1 - takeOf(side) / panelReach / 0.35), transition: ease, zIndex: 2 };
    }
    if (lean && lean !== side) {
      return { transform: `scale(${fly ? 0.4 : 1 - power * 0.35})`, opacity: fly ? 0 : 1 - power, transition: ease };
    }
    return { transition: ease };
  };
  const lean = x > 24 ? 'away' : x < -24 ? 'home' : null;
  const power = Math.min(1, Math.abs(x) / THROW_X);
  const top = card || slate[0];
  // the panel being dragged follows the finger over everything; its middle reaches the screen's middle as its black outline reaches the far edge
  const panelScale = fit ? fit['--pn-s'] : 1;
  const panelTip = fit ? parseFloat(fit['--pn-tip']) : 0;
  // upright phones have no scaled design: their outline reaches the far edge at 90% of the screen, and the back logo sits 34% from the middle
  const panelReach = fit ? 1083.9 + panelTip + 12 + window.innerWidth / panelScale / 2 : window.innerWidth * 0.9 + 12;
  const panelMiddle = fit ? 579.7 + parseFloat(fit['--pn-in']) + panelTip : window.innerWidth * 0.34;
  const takeFull = panelReach + 40;
  const takeOf = (side) => (fly ? (fly.side === side ? takeFull : 0) : lean === side ? Math.abs(drag.x) / panelScale : 0);
  const shiftOf = (take) => Math.min(1, take / panelReach) * panelMiddle;
  const versusSide = (g, side) => {
    const logo = (extra) => logoOf(g, side) && <img className={`pn-versus-logo ${extra} ${lg(g)}${bakedOf(g, side) ? ' pn-baked' : ''}${!extra && darkOf(g, side) ? ' pn-dark' : ''}`} style={{ ...fitOf(g, side), translate: g._sport === 'nhl' && g[`${side}_abbrev`] === 'OTT' ? 'calc(var(--pn-ott, -3) * var(--vw)) 0' : undefined }} src={(!extra && darkOf(g, side)) || bigOf(g, side) || logoOf(g, side)} onError={bigOf(g, side) ? bakedFailed(g, side) : undefined} alt="" draggable="false" decoding="sync" />;
    // two copies of the background logo, both always drawn: the clean one, and the tinted one over it that fades away as the panel takes over
    return <div className="pn-versus-side" style={{ '--team': colorOf(g, side) }}><div className="pn-versus-inner"><div className="pn-versus-logo-move">{logo('pn-versus-logo--clean')}<div className="pn-versus-logo-tint">{logo('')}</div></div></div></div>;
  };
  const sweep = fly ? Math.max(240, fly.dur - 80) : 0;
  const versusEdges = top ? [
    ...SIDES.map((side) => (
      <div key={`${top.id}-${side}`} className={`pn-versus-edge pn-versus-edge--${side}`} style={{ '--pn-take': `${takeOf(side)}px`, '--pn-shift': `${shiftOf(takeOf(side))}px`, '--pn-p': Math.min(1, takeOf(side) / panelReach), zIndex: takeOf(side) ? 3 : undefined, transition: drag.on && !fly ? 'none' : ['--pn-take', '--pn-shift', '--pn-p'].map((v) => (fly ? `${v} ${sweep}ms cubic-bezier(.25, .6, .35, 1)` : `${v} 0.3s ease`)).join(', ') }}>
        {versusSide(top, side)}
      </div>
    )),
    ...(flash && !fly ? [
      <div key={`${flash.game.id}-${flash.side}`} className={`pn-versus-edge pn-versus-edge--${flash.side} pn-versus-leaving`} style={{ '--pn-take': `${takeFull}px`, '--pn-shift': `${shiftOf(takeFull)}px`, '--pn-p': 1, zIndex: 3 }}>
        {versusSide(flash.game, flash.side)}
      </div>,
    ] : []),
  ] : null;
  const fitted = view === 'swipe' && !!fit;

  return (
    <>
      {banner}
      {view === 'swipe' && (
        <div className="pn-turn" role="alert">
          <div className="pn-turn-title"><img src={PN_LOGO} alt="Predict the Night" /></div>
          <p>{L('Turn your phone (or tablet) sideways to see the content', 'Tourne ton téléphone (ou ta tablette) sur le côté pour voir le contenu')}</p>
        </div>
      )}
      {fitted && <div className="pn-bleed" aria-hidden="true" />}
      <div className={`pn${fitted ? ' pn--fit' : ''}`} style={fitted ? fit : undefined} role="dialog" aria-modal="true" aria-label={L('Predict the night', 'Prédis la soirée')}>
        {/* the backdrop: a versus screen, each team's panel with its crest blown up, pointing at the middle */}
        {view === 'swipe' && top && (
          <div className="pn-bg pn-versus" aria-hidden="true" style={{ '--away': colorOf(top, 'away'), '--home': colorOf(top, 'home') }}>
            <div className="pn-versus-mid"><div className="pn-versus-v" /><div className="pn-versus-v pn-versus-v--small" /><div className="pn-versus-v pn-versus-v--up" /><div className="pn-versus-v pn-versus-v--up-small" /><div className="pn-versus-holo"><div className="pn-versus-holo-soft"><div className="pn-versus-holo-in"><div /></div></div></div><div className="pn-versus-holo pn-versus-holo--top"><div className="pn-versus-holo-soft"><div className="pn-versus-holo-in"><div /></div></div></div></div>
            {versusEdges}
            <span className="pn-vs">VS</span><div className="pn-versus-title"><img src={PN_LOGO} alt="Predict the Night" /></div>
            {/* old sticker: die-cut paper edge, faded warm ink, rubbed-off specks and scratches */}
            <svg width="0" height="0" style={{ position: 'absolute' }}>
              {/* same sticker, but grown then shrunk so the gaps between letters and lines fill in: one solid piece */}
              <filter id="pn-sticker-solid" x="-15%" y="-25%" width="130%" height="150%" colorInterpolationFilters="sRGB">
                <feMorphology in="SourceAlpha" operator="dilate" radius="22" />
                <feMorphology operator="erode" radius="14" />
                <feGaussianBlur stdDeviation="6" />
                <feComponentTransfer result="edge"><feFuncA type="linear" slope="30" intercept="-14" /></feComponentTransfer>
                <feImage href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' preserveAspectRatio='none'%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='0.35'%3E%3Cstop offset='0' stop-color='%2357b9ff'/%3E%3Cstop offset='0.22' stop-color='%23849eff'/%3E%3Cstop offset='0.45' stop-color='%23b783ff'/%3E%3Cstop offset='0.58' stop-color='%23e8a8ff'/%3E%3Cstop offset='0.76' stop-color='%235febd0'/%3E%3Cstop offset='1' stop-color='%235ec3ff'/%3E%3C/linearGradient%3E%3Crect width='100' height='100' fill='url(%23g)'/%3E%3C/svg%3E" preserveAspectRatio="none" />
                <feComposite in2="edge" operator="in" result="paper" />
                <feMerge><feMergeNode in="paper" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="pn-sticker-new" x="-15%" y="-15%" width="130%" height="130%" colorInterpolationFilters="sRGB">
                <feMorphology in="SourceAlpha" operator="dilate" radius="10" />
                <feGaussianBlur stdDeviation="2.2" />
                <feComponentTransfer result="edge"><feFuncA type="linear" slope="30" intercept="-1" /></feComponentTransfer>
                <feFlood floodColor="#c3c9d2" />
                <feComposite in2="edge" operator="in" result="paper" />
                {/* the sticker edge: silver foil, white shine bands, a light on its raised edge, glitter */}
                <feImage href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' preserveAspectRatio='none'%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.14' stop-color='%23ffffff' stop-opacity='0.5'/%3E%3Cstop offset='0.26' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.4' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.52' stop-color='%23ffffff' stop-opacity='0.6'/%3E%3Cstop offset='0.62' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.78' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.9' stop-color='%23ffffff' stop-opacity='0.45'/%3E%3Cstop offset='1' stop-color='%23ffffff' stop-opacity='0'/%3E%3C/linearGradient%3E%3Crect width='100' height='100' fill='url(%23g)'/%3E%3C/svg%3E" preserveAspectRatio="none" />
                <feComposite in2="edge" operator="in" result="sh" />
                <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="1" seed="7" />
                <feColorMatrix values="4 0 0 0 -1.5 0 4 0 0 -1.5 0 0 4 0 -1.5 12 0 0 0 -7" />
                <feComposite in2="edge" operator="in" result="gl" />
                <feGaussianBlur in="edge" stdDeviation="2.5" result="bump" />
                <feSpecularLighting in="bump" surfaceScale="6" specularConstant="1.1" specularExponent="22" lightingColor="#ffffff" result="lit"><fePointLight x="-100" y="-150" z="300" /></feSpecularLighting>
                <feComposite in="lit" in2="edge" operator="in" result="glare" />
                <feMerge result="paper"><feMergeNode in="paper" /><feMergeNode in="sh" /><feMergeNode in="glare" /><feMergeNode in="gl" /></feMerge>
                <feMorphology in="SourceAlpha" operator="dilate" radius="0.8" />
                <feGaussianBlur stdDeviation="0.6" />
                <feComponentTransfer result="line"><feFuncA type="linear" slope="8" intercept="-1" /></feComponentTransfer>
                <feGaussianBlur in="line" stdDeviation="1.2" />
                <feOffset dy="1" />
                <feComponentTransfer result="shade"><feFuncA type="linear" slope="0.85" /></feComponentTransfer>
                <feFlood floodColor="#101216" />
                <feComposite in2="line" operator="in" result="outline" />
                <feMerge><feMergeNode in="paper" /><feMergeNode in="shade" /><feMergeNode in="outline" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="pn-tint" colorInterpolationFilters="sRGB"><feColorMatrix type="saturate" values="0.72" /><feColorMatrix type="matrix" values="0.86 0 0 0 0.04  0 0.8 0 0 0.04  0 0 0.66 0 0.03  0 0 0 1 0" /></filter>
              <filter id="pn-sticker-title" x="-15%" y="-15%" width="130%" height="130%" colorInterpolationFilters="sRGB">
                <feMorphology in="SourceAlpha" operator="dilate" radius="8" />
                <feGaussianBlur stdDeviation="2.2" />
                <feComponentTransfer result="edge"><feFuncA type="linear" slope="30" intercept="-1" /></feComponentTransfer>
                <feFlood floodColor="#c3c9d2" />
                <feComposite in2="edge" operator="in" result="paper" />
                {/* the sticker edge: silver foil, white shine bands, a light on its raised edge, glitter */}
                <feImage href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' preserveAspectRatio='none'%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.14' stop-color='%23ffffff' stop-opacity='0.5'/%3E%3Cstop offset='0.26' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.4' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.52' stop-color='%23ffffff' stop-opacity='0.6'/%3E%3Cstop offset='0.62' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.78' stop-color='%23ffffff' stop-opacity='0'/%3E%3Cstop offset='0.9' stop-color='%23ffffff' stop-opacity='0.45'/%3E%3Cstop offset='1' stop-color='%23ffffff' stop-opacity='0'/%3E%3C/linearGradient%3E%3Crect width='100' height='100' fill='url(%23g)'/%3E%3C/svg%3E" preserveAspectRatio="none" />
                <feComposite in2="edge" operator="in" result="sh" />
                <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="1" seed="7" />
                <feColorMatrix values="4 0 0 0 -1.5 0 4 0 0 -1.5 0 0 4 0 -1.5 12 0 0 0 -7" />
                <feComposite in2="edge" operator="in" result="gl" />
                <feGaussianBlur in="edge" stdDeviation="2.5" result="bump" />
                <feSpecularLighting in="bump" surfaceScale="6" specularConstant="1.1" specularExponent="22" lightingColor="#ffffff" result="lit"><fePointLight x="-100" y="-150" z="300" /></feSpecularLighting>
                <feComposite in="lit" in2="edge" operator="in" result="glare" />
                <feMerge result="paper"><feMergeNode in="paper" /><feMergeNode in="sh" /><feMergeNode in="glare" /><feMergeNode in="gl" /></feMerge>
                <feMerge><feMergeNode in="paper" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="pn-sticker" x="-15%" y="-15%" width="130%" height="130%" colorInterpolationFilters="sRGB">
                <feMorphology in="SourceAlpha" operator="dilate" radius="24" result="fat" />
                <feGaussianBlur in="fat" stdDeviation="6" />
                <feComponentTransfer result="edge"><feFuncA type="linear" slope="30" intercept="-1" /></feComponentTransfer>
                <feFlood floodColor="#e6dcc3" />
                <feComposite in2="edge" operator="in" result="paper" />
                <feColorMatrix in="SourceGraphic" type="saturate" values="0.72" />
                <feColorMatrix type="matrix" values="0.86 0 0 0 0.04  0 0.8 0 0 0.04  0 0 0.66 0 0.03  0 0 0 1 0" result="ink" />
                <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="2" seed="4" />
                <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  10 0 0 0 -6.4" result="specks" />
                <feTurbulence type="fractalNoise" baseFrequency="0.012 0.32" numOctaves="2" seed="9" />
                <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  12 0 0 0 -8" result="scratches" />
                <feMerge result="wear"><feMergeNode in="specks" /><feMergeNode in="scratches" /></feMerge>
                <feComposite in="ink" in2="wear" operator="out" result="worn" />
                <feMorphology in="SourceAlpha" operator="dilate" radius="2.5" />
                <feGaussianBlur stdDeviation="1" />
                <feComponentTransfer result="line"><feFuncA type="linear" slope="8" intercept="-1" /></feComponentTransfer>
                <feGaussianBlur in="line" stdDeviation="3" />
                <feOffset dy="2" />
                <feComponentTransfer><feFuncA type="linear" slope="0.6" /></feComponentTransfer>
                <feComposite in2="SourceAlpha" operator="out" result="shade" />
                <feFlood floodColor="#101216" />
                <feComposite in2="line" operator="in" />
                <feComposite in2="SourceAlpha" operator="out" result="outline" />
                <feMerge><feMergeNode in="paper" /><feMergeNode in="shade" /><feMergeNode in="outline" /><feMergeNode in="worn" /></feMerge>
              </filter>
            </svg>
          </div>
        )}
        {view === 'enter' && <div className="pn-bg pn-bg--enter" aria-hidden="true" />}

        <div className="pn-top">
          <button type="button" className="pn-icon" onClick={() => setView(null)} aria-label={L('Close', 'Fermer')}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          <div className="pn-progress" aria-label={`${picked}/${slate.length}`} style={view === 'enter' ? { visibility: 'hidden' } : undefined}>
            {slate.map((g) => <span key={g.id} className={picks[String(g.id)] && !redo.includes(String(g.id)) ? 'on' : ''} />)}
          </div>
          <span className="pn-count">{view === 'swipe' ? `${Math.min(picked + 1, slate.length)}/${slate.length}` : ''}</span>
        </div>
        {msg && <div className="pn-msg" role="status">{msg}</div>}

        {view === 'enter' && (
          <div className="pn-enter">
            <div className="pn-enter-main">
              <h2 className="pn-enter-title">{L('Predict', 'Prédis')}<br />{L('the night', 'la soirée')}</h2>
              <div className="pn-board-row">
                <div className="pn-stat"><span className="pn-tile pn-tile--white">{L('Prize', 'Prix')}</span><b>{money(PRIZE)}</b></div>
                <div className="pn-stat"><span className="pn-tile pn-tile--white">{L('Players', 'Joueurs')}</span><b>{nightEntries.length}</b></div>
                <div className="pn-stat"><span className="pn-tile pn-tile--white">{L('Games', 'Matchs')}</span><b>{slate.length}</b></div>
              </div>
              <p className="pn-enter-line">{L(`${money(FEE)} to get in. Most right picks wins ${money(PRIZE)} — a tie splits it.`, `${money(FEE)} pour entrer. Le plus de bons choix gagne ${money(PRIZE)} — égalité, on partage.`)}</p>
              {nightEntries.length > 0 && (
                <div className="pn-enter-players">
                  {nightEntries.map((e) => <span key={e.user_id} className="pn-tile">{names[e.user_id] || '—'}</span>)}
                </div>
              )}
              <button type="button" className="pn-tile pn-tile--orange pn-enter-btn" onClick={enter} disabled={paying || (!practice && balance < FEE)}>
                {paying ? L('Paying…', 'Paiement…') : practice ? L('Practice (not saved)', 'Pratique (non enregistré)') : `${L('Enter', 'Entrer')} · ${money(FEE)}`}
              </button>
              {!practice && balance < FEE && <p className="pn-note">{L('Your balance is too low to enter.', 'Ton solde est trop bas pour entrer.')}</p>}
            </div>
          </div>
        )}

        {view === 'swipe' && (
          card ? (
            <>
              <div className="pn-stage">
                {deck.slice(0, 3).reverse().map((g) => {
                  const depth = deck.indexOf(g);
                  const isTop = depth === 0;
                  const style = isTop
                    ? {}
                    : { transform: `translateY(${depth * 18 - (lean ? power * 8 : 0)}px) scale(${1 - depth * 0.06 + (lean ? power * 0.03 : 0)})` };
                  return (
                    <div key={g.id} className={`pn-card${isTop ? ' top' : ''}${fly && isTop ? ' thrown' : ''}`} style={style}
                      onPointerDown={isTop ? down : undefined} onPointerMove={isTop ? move : undefined} onPointerUp={isTop ? up : undefined} onPointerCancel={isTop ? up : undefined}>
                      <div className="pn-card-meta" style={isTop && lean ? { opacity: 1 - power, transition: drag.on && !fly ? 'none' : 'opacity 0.15s ease' } : { transition: 'opacity 0.2s ease' }}><b>{fmtDay(g)}</b><span>{fmtHour(g)}</span>{recordOf(g, 'away') && recordOf(g, 'home') && <div className="pn-card-records"><span>{recordOf(g, 'away')}</span><span>{recordOf(g, 'home')}</span></div>}</div>
                      <div className="pn-teams">
                        {SIDES.map((side) => (
                          <div key={side} className={`pn-team${isTop && lean === side ? ' lean' : ''}`} style={{ '--team': colorOf(g, side), '--plate': teamColor2(g._sport, g[`${side}_abbrev`]) || colorOf(g, side), ...(isTop ? teamStyle(side) : {}) }}>
                            <div className="pn-logo-wrap">
                              {logoOf(g, side) ? <img className={`pn-logo ${lg(g)}${bakedOf(g, side) ? ' pn-baked' : ''}`} style={fitOf(g, side)} src={bakedOf(g, side) || logoOf(g, side)} alt="" draggable="false" decoding="sync" onError={(e) => { if (e.currentTarget.classList.contains('pn-baked')) bakedFailed(g, side)(e); else e.currentTarget.style.visibility = 'hidden'; }} /> : <span className="pn-logo pn-logo--none">{g[`${side}_abbrev`]}</span>}
                            </div>
                            <span className="pn-abbrev">{g[`${side}_abbrev`]}</span>
                            <span className="pn-name">{g[`${side}_team`]}</span>
                            {recordOf(g, side) && <span className="pn-record">{recordOf(g, side)}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pn-controls">
                <button type="button" className="pn-pick" style={{ '--team': colorOf(card, 'away') }} onClick={() => choose('away')} aria-label={`${card.away_team} ${L('wins', 'gagne')}`}>
                  {logoOf(card, 'away') ? <img key={card.id} className={`pn-pick-logo ${lg(card)}`} src={logoOf(card, 'away')} alt="" /> : card.away_abbrev}
                </button>
                <button type="button" className="pn-undo" onClick={undo} disabled={!picked} aria-label={L('Undo', 'Annuler')}>
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 14L4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" /></svg>
                </button>
                <button type="button" className="pn-pick" style={{ '--team': colorOf(card, 'home') }} onClick={() => choose('home')} aria-label={`${card.home_team} ${L('wins', 'gagne')}`}>
                  {logoOf(card, 'home') ? <img key={card.id} className={`pn-pick-logo ${lg(card)}`} src={logoOf(card, 'home')} alt="" /> : card.home_abbrev}
                </button>
              </div>
              <p className="pn-hint">{L('Swipe toward the winner', 'Glisse vers le gagnant')}</p>
            </>
          ) : (
            <div className="pn-empty">{L('Nothing left to pick.', 'Plus rien à choisir.')}</div>
          )
        )}

      </div>
    </>
  );
}
